import { t, Trans } from '@lingui/macro';
import React, { useEffect, useMemo, useState } from 'react';

import { CommonFaceResponse, getCommonFaces, splitIdentity } from '@api/v4/private/people_tagging/faces';
import { amplitudeService } from '../../../services/amplitude';
import { Locale } from '@components/common/language_menu/languagesMap';
import { BulkCustomFieldsList } from '@components/show_page/bulk_actions/sub_components/BulkCustomFieldsList';
import BulkPeopleContent from '@components/show_page/bulk_actions/sub_components/BulkPeopleContent';
import TagsModalContent from '@components/show_page/bulk_actions/sub_components/TagsModalContent';
import { unsavedChangesOptions } from '@helpers/sweet_alert_options';

import renderModal from '../modals/renderModal';

enum TabType {
  Tags = 'tags',
  People = 'people',
  CustomFields = 'customFields',
}

interface BulkTagWrapperProps {
  closeModal: () => void;
  selectedAssetKeys: Set<string>;
  ugtLocale?: Locale;
}

const BulkTagWrapper: React.FC<BulkTagWrapperProps> = ({
  closeModal,
  selectedAssetKeys,
  ugtLocale,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>(TabType.Tags);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removedFaceIds, setRemovedFaceIds] = useState<Set<string>>(new Set());
  const [facesToSplit, setFacesToSplit] = useState<string[][]>([]);
  const [isLoadingFaces, setIsLoadingFaces] = useState(false);
  const [faces, setFaces] = useState<CommonFaceResponse[]>([]);
  const [hasFetchedFaces, setHasFetchedFaces] = useState(false);

  useEffect(() => {
    // Track Bulk Tag modal page load with Amplitude
    if (window.BFG?.amplitude?.enabled && window.amplitudeService) {
      window.amplitudeService.trackEvent('PageLoad', {
        elementName: 'BulkTagView',
        elementLocation: 'BulkTagModal',
        productArea: 'BulkActionBar',
        featureArea: 'BulkTagModal',
        eventName: 'PageLoad',
        'key properties': {
          assetIds: Array.from(selectedAssetKeys || [])
        }
      });
    }

    return () => { closeModal(); };
  }, [closeModal, selectedAssetKeys]);

  // Fetch faces when People tab is activated
  useEffect(() => {
    if (activeTab === TabType.People && !hasFetchedFaces) {
      setIsLoadingFaces(true);
      getCommonFaces({
        brandfolderKey: BFG.resource.key,
        assetKeys: Array.from(selectedAssetKeys),
      })
        .then((data) => {
          setFaces(data);
          setHasFetchedFaces(true);
        })
        .catch((error) => {
          console.error('Error fetching faces:', error);
          Notify.create({
            title: t`Failed to load faces. Please try again.`,
            type: 'error',
          });
        })
        .finally(() => {
          setIsLoadingFaces(false);
        });
    }
  }, [activeTab, hasFetchedFaces, selectedAssetKeys]);

  const trackBulkCustomFieldsTabSelect = (): void => {
    if (window.BFG?.amplitude?.enabled && window.amplitudeService) {
      window.amplitudeService.trackEvent('TabSelect', {
        elementName: 'BulkCustomFieldsTab',
        elementLocation: 'BulkTagModal',
        productArea: 'BulkActionBar',
        featureArea: 'BulkTagModal',
        eventName: 'TabSelect',
        'key properties': {
          assetIds: Array.from(selectedAssetKeys || [])
        }
      });
    }
  };

  const toggleTab = (tab: TabType, onSwitched?: () => void): void => {
    const afterSwitch = (): void => {
      if (tab === TabType.CustomFields) {
        trackBulkCustomFieldsTabSelect();
      }
      onSwitched?.();
    };

    if (hasUnsavedChanges) {
      window.swal(unsavedChangesOptions({
        onLeaveCopy: t`Discard Changes`,
        onStayCopy: t`Continue Editing`,
      }), (confirm: boolean) => {
        if (confirm) {
          setActiveTab(tab);
          setHasUnsavedChanges(false);
          setRemovedFaceIds(new Set());
          setFacesToSplit([]);
          afterSwitch();
        }
      });
    } else {
      setActiveTab(tab);
      setHasUnsavedChanges(false);
      afterSwitch();
    }
  };

  const handleClose = (): void => {
    if (hasUnsavedChanges) {
      window.swal(unsavedChangesOptions({
        onStayCopy: t`Continue Editing`,
      }), (confirm: boolean) => {
        if (confirm) {
          closeModal();
        }
      });
    } else {
      closeModal();
    }
  };

  const handleRemoveFace = (faceIds: string[]): void => {
    setRemovedFaceIds((prev) => {
      const updated = new Set(prev);
      faceIds.forEach((id) => updated.add(id));
      return updated;
    });
    setFacesToSplit((prev) => {
      // Check if this face group is already in the list
      const faceIdsStr = faceIds.sort().join(',');
      const alreadyExists = prev.some(
        (existingFaceIds) => existingFaceIds.sort().join(',') === faceIdsStr
      );
      if (alreadyExists) {
        return prev;
      }
      return [...prev, faceIds];
    });
    setHasUnsavedChanges(true);
  };

  const handleSave = async (): Promise<void> => {
    setIsSubmitting(true);

    try {
      // Make a separate API call for each face group to split (remove identity from)
      const promises = facesToSplit.map((faceIds) =>
        splitIdentity({
          brandfolderKey: BFG.resource.key,
          faceIds,
        })
      );

      await Promise.all(promises);

      if (
        facesToSplit.length > 0 &&
        window.BFG?.hasFeature?.('people_tagging') &&
        amplitudeService.isReady()
      ) {
        const identityIds = new Set<string>();
        for (const faceIdsGroup of facesToSplit) {
          const groupKey = [...faceIdsGroup].sort().join(',');
          const match = faces.find((f) => [...f.face_ids].sort().join(',') === groupKey);
          if (match?.identity_id) {
            identityIds.add(match.identity_id);
          }
        }
        const identityIdForEvent = identityIds.size === 1 ? [...identityIds][0] : null;
        const eventArea = 'AppFrontend';
        const eventName = 'ButtonClick';
        const productArea = 'BulkActionBar';
        const featureArea = 'BulkActionBar';
        const elementName = 'BulkPeopleTag';
        const elementLocation = 'BulkActionBar';
        const eventFull = `${eventArea}.${productArea}.${featureArea}.${elementName}.${eventName}`;
        amplitudeService.trackEvent(eventName, {
          eventArea,
          productArea,
          featureArea,
          elementLocation,
          elementName,
          eventName,
          eventFull,
          brandfolderId: window.BFG?.brandfolder_key ?? undefined,
          organizationId: window.BFG?.organization_key ?? undefined,
          userId: window.BFG?.currentUser?.user_id ?? undefined,
          userRole: window.BFG?.currentUser?.role ?? undefined,
          actionType: 'bulk_remove_tag',
          assetCount: selectedAssetKeys.size,
          identityId: identityIdForEvent,
        });
      }

      Notify.create({
        title: t`Processing people tag updates!`,
        body: t`Updates will be available shortly.`,
        type: 'success',
      });
      // Remove the deleted faces from state so they don't reappear
      setFaces((prevFaces) => prevFaces.filter(
        (face) => !face.face_ids.some((id) => removedFaceIds.has(id))
      ));
      setHasUnsavedChanges(false);
      setRemovedFaceIds(new Set());
      setFacesToSplit([]);
    } catch (error) {
      console.error('Error removing people tags:', error);
      Notify.create({
        title: t`Failed to remove people tags. Please try again.`,
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasCustomFields = BFG.context.hasFeature('custom_fields');
  const assetCount = selectedAssetKeys.size;

  // Memoize visible faces to avoid recalculating on every render
  const visibleFaces = useMemo(() => (
    faces.filter((face) => !face.face_ids.some((id) => removedFaceIds.has(id)))
  ), [faces, removedFaceIds]);

  const peopleTabId = 'bulk-tag-people-tab';
  const tagsTabId = 'bulk-tag-tags-tab';
  const customFieldsTabId = 'bulk-tag-custom-fields-tab';
  const peopleTabPanelId = 'bulk-tag-people-tabpanel';
  const tagsTabPanelId = 'bulk-tag-tags-tabpanel';
  const customFieldsTabPanelId = 'bulk-tag-custom-fields-tabpanel';

  const tabIdForType = (tab: TabType): string => {
    switch (tab) {
      case TabType.People:
        return peopleTabId;
      case TabType.Tags:
        return tagsTabId;
      case TabType.CustomFields:
        return customFieldsTabId;
      default:
        return tagsTabId;
    }
  };

  const visibleTabsInOrder = useMemo((): TabType[] => {
    const tabs: TabType[] = [];
    if (BFG.context.hasFeature('people_tagging') && BFG.brandfolderSettings.people_tagging) {
      tabs.push(TabType.People);
    }
    tabs.push(TabType.Tags);
    if (hasCustomFields) {
      tabs.push(TabType.CustomFields);
    }
    return tabs;
  }, [hasCustomFields]);

  const focusTabElement = (tab: TabType): void => {
    window.setTimeout(() => {
      document.getElementById(tabIdForType(tab))?.focus();
    }, 0);
  };

  const handleTabKeyDown = (e: React.KeyboardEvent, tab: TabType): void => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      toggleTab(tab);
      return;
    }

    const order = visibleTabsInOrder;
    const idx = order.indexOf(tab);
    if (idx === -1) {
      return;
    }

    const goTo = (next: TabType): void => {
      if (next === tab) {
        return;
      }
      e.preventDefault();
      toggleTab(next, () => focusTabElement(next));
    };

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      goTo(order[(idx + 1) % order.length]);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      goTo(order[(idx - 1 + order.length) % order.length]);
    } else if (e.key === 'Home') {
      goTo(order[0]);
    } else if (e.key === 'End') {
      goTo(order[order.length - 1]);
    }
  };

  return (
    <div className="bulk-tag-wrapper">
      {/* WAI-ARIA tabs: selected tab tabIndex 0, others -1; use Arrow keys / Home / End to move focus between tabs. */}
      <div
        aria-label={t`Bulk tag options`}
        className="modal-content-wrapper__header wrapper-headers"
        role="tablist"
      >
        {BFG.context.hasFeature('people_tagging') && BFG.brandfolderSettings.people_tagging && (
          <div
            aria-controls={peopleTabPanelId}
            aria-selected={activeTab === TabType.People}
            className={`header ${activeTab === TabType.People ? 'active' : ''}`}
            id={peopleTabId}
            onClick={() => toggleTab(TabType.People)}
            onKeyDown={(e) => handleTabKeyDown(e, TabType.People)}
            role="tab"
            tabIndex={activeTab === TabType.People ? 0 : -1}
          >
            <span aria-hidden="true" className="bff-people icon" />
            <h3 className="modal-title"><Trans>People</Trans></h3>
          </div>
        )}
        <div
          aria-controls={tagsTabPanelId}
          aria-selected={activeTab === TabType.Tags}
          className={`header ${activeTab === TabType.Tags ? 'active' : ''} ${!hasCustomFields ? 'one-tab' : ''}`}
          id={tagsTabId}
          onClick={() => toggleTab(TabType.Tags)}
          onKeyDown={(e) => handleTabKeyDown(e, TabType.Tags)}
          role="tab"
          tabIndex={activeTab === TabType.Tags ? 0 : -1}
        >
          <span aria-hidden="true" className="bff-tag icon" />
          <h3 className="modal-title"><Trans>Bulk Tag</Trans></h3>
        </div>
        {hasCustomFields && (
          <div
            aria-controls={customFieldsTabPanelId}
            aria-selected={activeTab === TabType.CustomFields}
            className={`header ${activeTab === TabType.CustomFields ? 'active' : ''}`}
            id={customFieldsTabId}
            onClick={() => toggleTab(TabType.CustomFields)}
            onKeyDown={(e) => handleTabKeyDown(e, TabType.CustomFields)}
            role="tab"
            tabIndex={activeTab === TabType.CustomFields ? 0 : -1}
          >
            <span aria-hidden="true" className="bff-custom-fields icon" />
            <h3 className="modal-title"><Trans>Bulk Custom Fields</Trans></h3>
          </div>
        )}
      </div>
      {activeTab === TabType.People && BFG.context.hasFeature('people_tagging') ? (
        <div
          aria-labelledby={peopleTabId}
          id={peopleTabPanelId}
          role="tabpanel"
        >
          <BulkPeopleContent
            assetCount={assetCount}
            handleClose={handleClose}
            handleRemoveFace={handleRemoveFace}
            handleSave={handleSave}
            isLoadingFaces={isLoadingFaces}
            isSubmitting={isSubmitting}
            removedFaceIdsCount={removedFaceIds.size}
            visibleFaces={visibleFaces}
          />
        </div>
      ) : activeTab === TabType.CustomFields ? (
        <div
          aria-labelledby={customFieldsTabId}
          id={customFieldsTabPanelId}
          role="tabpanel"
        >
          <BulkCustomFieldsList
            closeModal={closeModal}
            hasUnsavedChanges={hasUnsavedChanges}
            selectedAssetKeys={selectedAssetKeys}
            setHasUnsavedChanges={setHasUnsavedChanges}
            ugtLocale={ugtLocale as Locale | undefined}
          />
        </div>
      ) : (
        <div
          aria-labelledby={tagsTabId}
          id={tagsTabPanelId}
          role="tabpanel"
        >
          <TagsModalContent
            closeModal={closeModal}
            hasUnsavedChanges={hasUnsavedChanges}
            selectedAssetKeys={selectedAssetKeys}
            setHasUnsavedChanges={setHasUnsavedChanges}
            ugtLocale={ugtLocale}
          />
        </div>
      )}
    </div>
  );
};

const ModalComponent = renderModal(BulkTagWrapper, 'BulkTagWrapper');
export default ModalComponent;
